import React from 'react';
import {
    all,
    fork,
    take,
    put,
    cancel,
    select,
    call,
    actionChannel,
    delay,
} from 'redux-saga/effects';

import isPlainObject from 'lodash/isPlainObject';
import parseInt from 'lodash/parseInt';
import shuffle from 'lodash/shuffle';
import { ulid } from 'ulid';
import { saveTokenCart, loadTokenCart } from '@ts/utils/cs';
import { InternalUserValidationError, ExternalUserValidationError } from '@ts/utils/errors';
import { initialState as cartInitialState } from '@ts/core/reducers/cart';
import { AnalyticsInstance } from '@ts/utils/analytics';
import { get, LS_CART_ITEM_PRODUCT_IDS } from '@ts/utils/ls';
import { ToastContent, callToast } from '@ts/commons/Toast';
import * as activityPageActions from '@ts/containers/ActivityPage/actions';
import MoveToElement from '@ts/utils/moveTo';
import api from '@ts/api';
import actions from '@ts/core/actions';
import selectors from '@ts/core/selectors';
import {
    trackPromoCodeRequest,
    trackUpdateCart,
    convCartItemsToPurchaseItems,
} from '@ts/utils/iterableUtils';

const cartDataInitial = {
    cart_id: cartInitialState.data.id,
    custom_total: cartInitialState.data.customTotal,
    discount: cartInitialState.data.discount,
    hotels_count: cartInitialState.data.hotelsCount,
    lifetime: cartInitialState.data.lifeTime,
    subtotal: cartInitialState.data.subTotal,
    surcharges: cartInitialState.data.surcharges,
    time_left: cartInitialState.data.timeLeft,
    total: cartInitialState.data.total,
    trips_count: cartInitialState.data.tripsCount,
    subtotal_subunits: cartInitialState.data.subTotalSubunits,
    surcharges_subunits: cartInitialState.data.surchargesSubunits,
    total_subunits: cartInitialState.data.totalSubunits,
};

export function* onFetch() {
    try {
        yield put(actions.cart.changeIsLoading(true));
        yield put(actions.cart.changeIsError(false));
        yield put(actions.cart.changeErrorMessage(''));

        yield delay(150);

        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const cart = yield api.cart.fetch({
            session_id: sessionId,
        });

        if (!cart.success) {
            yield put(actions.cart.changeData(cartDataInitial));
            yield put(actions.cart.changeItems([]));
        } else {
            const cartItems = cart.data.cart_items || [];
            yield put(actions.cart.changeData(cart.data));
            yield put(actions.cart.changeItems(cartItems));
        }
    } catch (err) {
        yield put(actions.cart.changeIsError(true));
        yield put(actions.cart.changeErrorMessage(err.message));
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeIsLoaded(true));
        yield put(actions.cart.changeIsLoading(false));
        yield put(actions.cart.onEndFetch());
    }
}

export function* onBackgroundFetch() {
    try {
        yield put(actions.cart.changeIsLoading(true));
        yield delay(150);
        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const cart = yield api.cart.fetch({
            session_id: sessionId,
        });
        if (!cart.success) {
            yield put(actions.cart.changeData(cartDataInitial));
            yield put(actions.cart.changeItems([]));
        } else {
            yield put(actions.cart.changeData(cart.data));
            yield put(actions.cart.changeItems(cart.data.cart_items));
        }
        yield put(actions.cart.changeIsError(false));
        yield put(actions.cart.changeErrorMessage(''));
    } catch (err) {
        yield put(actions.cart.changeIsError(true));
        yield put(actions.cart.changeErrorMessage(err.message));
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeIsLoaded(true));
        yield put(actions.cart.changeIsLoading(false));
        yield put(actions.cart.onEndBackgroundFetch());
    }
}

export function* watchEndFetch() {
    let task;
    let prevmark;
    let previd;
    while (true) {
        const { payload } = yield take([
            actions.cart.ActionTypes.ON_END_BACKGROUND_FETCH,
            actions.cart.ActionTypes.ON_END_FETCH,
        ]);

        if (task) {
            yield cancel(task);
            task = undefined;
        }

        yield delay(1000);

        const cartData = yield select(selectors.cart.makeSelectData());
        const cartItems = yield select(selectors.cart.makeSelectItems());

        if (prevmark !== cartData.expireTime && (cartData.id || (!cartData.id && previd))) {
            prevmark = cartData.expireTime;
            previd = cartData.id;
            if (!payload || !payload.dontSendToIterable) {
                trackUpdateCart(
                    convCartItemsToPurchaseItems(
                        cartItems.map((item) => ({
                            ...item,
                            trip_name: item.product_name,
                            activity: { id: item.product_id, slug: item.product_slug },
                        }))
                    )
                );
            }
        }
    }
}

export function* watchFetch() {
    let task;
    while (true) {
        const action = yield take([
            actions.cart.ActionTypes.FETCH,
            actions.cart.ActionTypes.BACKGROUND_FETCH,
            // cancel by change session
            actions.cart.ActionTypes.CHANGE_SESSION_ID,
        ]);

        if (task) {
            yield cancel(task);
            task = undefined;
        }

        if (action.type === actions.cart.ActionTypes.FETCH) {
            task = yield fork(onFetch);
        } else if (action.type === actions.cart.ActionTypes.BACKGROUND_FETCH) {
            task = yield fork(onBackgroundFetch);
        }
    }
}

export function* onAddResolve({ promise, activity, optional }) {
    try {
        const items = yield select(selectors.cart.makeSelectItems());
        const isLoaded = yield select(selectors.cart.makeSelectIsLoaded());
        // prevents sad state to force loading
        // or visible loaded items & reload state
        yield put(actions.cart.changeIsLoaded(items.length > 0 && isLoaded));

        const res = yield promise;
        yield put(actions.cart.concatActivities([activity]));

        if (res.success) {
            yield put(
                actions.cart.changeLifeTime({
                    lifeTime: res.data.lifetime,
                    timeLeft: res.data.time_left,
                })
            );

            try {
                const analyticsTickets = Object.keys(optional.data.selectedScheduleTickets)
                    .filter(
                        (ticketId) =>
                            optional.data.selectedScheduleTickets[ticketId] &&
                            optional.data.selectedScheduleTickets[ticketId] > 0
                    )
                    .map((ticketId) =>
                        optional.data.selectedSchedule.tickets.find(
                            (ticket) => parseInt(ticket.id, 10) === parseInt(ticketId, 10)
                        )
                    )
                    .filter((ticket) => Boolean(ticket))
                    .map((ticket) => ({
                        id: ticket.id,
                        price: ticket.price,
                        price_strike_out: ticket.strike_out,
                        description: ticket.description,
                        name: ticket.name,
                        booked: optional.data.selectedScheduleTickets[ticket.id],
                    }));

                const analyticsPayload = AnalyticsInstance.creators.createProductsInCartFromCartItems(
                    {
                        categoryName: '',
                    }
                )([
                    {
                        product_id: activity.id,
                        product_city: optional.destination.name,
                        product_name: activity.name,
                        product_activity: activity.name,
                        partner_title: activity.title,
                        tickets: analyticsTickets,
                    },
                ]);

                AnalyticsInstance.product.addToCart(analyticsPayload);
            } catch (err) {
                console.log(err);
            }

            // if out of sync happens, we apply factual data, and hope that the fetch will work correctly
            const sessionId = yield select(selectors.cart.makeSelectSessionId());
            if (sessionId !== res.data.session_id) {
                saveTokenCart(res.data.session_id);
                yield put(actions.cart.changeSessionId(res.data.session_id));
                yield put(actions.cart.changeId(res.data.cart_id));
            }
        }
    } catch (err) {
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.fetch());
    }
}

export function* watchAddResolve() {
    // debounced,
    // it is not yet clear whether this is necessary here,
    // probably not.
    // and you need to do the execution in turn,
    // using accumulating channels
    let task;
    while (true) {
        const action = yield take(actions.cart.ActionTypes.ADD_RESOLVE);
        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onAddResolve, action);
    }
}

export function* watchApplyDiscount() {
    let task;
    while (true) {
        const action = yield take(actions.cart.ActionTypes.APPLY_DISCOUNT);
        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onApplyDiscount, action);
    }
}

export function* watchRemoveDiscount() {
    let task;
    while (true) {
        const action = yield take(actions.cart.ActionTypes.REMOVE_DISCOUNT);
        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onRemoveDiscount, action);
    }
}

export function* onApplyDiscount() {
    try {
        yield put(actions.cart.changeIsApplying(true));
        yield put(actions.cart.changeIsApplyingError(false));
        const promocodeValue = yield select(selectors.cart.makeSelectPromocodeValue());
        const sessionId = yield select(selectors.cart.makeSelectSessionId());

        if (!promocodeValue) {
            throw new InternalUserValidationError('Enter your promo code.');
        }

        const res = yield api.promocode.apply({
            session_id: sessionId,
            code: promocodeValue,
        });

        if (!res.success) {
            trackPromoCodeRequest({
                success: false,
                name: '',
                code: promocodeValue,
            });
            const errors = Array.isArray(res.errors)
                ? res.errors
                : ['Failed to apply discount to cart'];

            throw new ExternalUserValidationError(errors.join(', '));
        } else {
            yield put(actions.cart.backgroundFetch());
            yield take([
                actions.cart.ActionTypes.ON_END_FETCH,
                actions.cart.ActionTypes.ON_END_BACKGROUND_FETCH,
            ]);

            const cartData = yield select(selectors.cart.makeSelectData());
            if (typeof cartData?.discount?.promo?.code === 'string') {
                trackPromoCodeRequest({
                    success: true,
                    name: cartData?.discount?.promo?.name || '',
                    code: cartData?.discount?.promo?.code,
                });
            }
        }
    } catch (err) {
        yield put(actions.cart.changeIsApplyingError(true));
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeIsApplying(false));
    }
}

export function* onRemoveDiscount() {
    try {
        yield put(actions.promocode.changeValue(''));
        yield put(actions.cart.changeIsApplying(true));
        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const res = yield api.promocode.remove({
            session_id: sessionId,
        });
        if (!res.success) {
            const errors = Array.isArray(res.errors)
                ? res.errors
                : ['Failed to remove discount from cart'];

            throw new ExternalUserValidationError(errors.join(', '));
        } else {
            yield put(actions.cart.backgroundFetch());
        }
    } catch (err) {
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeIsApplying(false));
    }
}

export function* watchExtend() {
    let task;
    while (true) {
        const action = yield take(actions.cart.ActionTypes.EXTEND);
        if (task) {
            yield cancel(task);
            task = undefined;
        }

        task = yield fork(onExtend, action);
    }
}

export function* onExtend() {
    try {
        yield put(actions.cart.changeIsExtending(true));
        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const res = yield api.cart.extend({ session_id: sessionId });
        if (res.success) {
            const cartData = yield select(selectors.cart.makeSelectData());
            const expireDiff = yield select(selectors.cart.makeSelectExpireDiff());
            const { lifeTime } = cartData;
            const nextTimeLeft = expireDiff.totalSeconds + 300;
            const lifeTimeSeconds = lifeTime * 60;
            yield put(
                actions.cart.changeLifeTime({
                    lifeTime: cartData.lifeTime,
                    timeLeft: nextTimeLeft > lifeTimeSeconds ? lifeTimeSeconds : nextTimeLeft,
                })
            );
            yield put(actions.cart.changeExpireModalIsVisible(false));
        } else {
            const errors = Array.isArray(res.errors) ? res.errors : ['Failed to extend time cart'];

            throw new ExternalUserValidationError(errors.join(', '));
        }
    } catch (err) {
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeIsExtending(false));
    }
}

export function* fetchOutdated() {
    const itemProductIds = yield select(selectors.cart.makeSelectItemProductIds());
    let successfullyReceivedActivities = [];
    const promises = itemProductIds.map((item) => {
        return api.activities.fetchById(item);
    });
    yield (async () => {
        const response = await Promise.allSettled(promises);
        successfullyReceivedActivities = response.map((activities) => {
            if (activities.status === 'fulfilled') {
                return activities.value;
            } else if (activities.status === 'rejected') {
                put(actions.app.error(activities.reason));
            }
        });
        successfullyReceivedActivities.reverse();
    })();
    yield put(actions.cart.concatActivities(successfullyReceivedActivities));
    yield put(actions.cart.changeIsOutdatedLoaded(true));
}

export function* watchExpireTick() {
    while (true) {
        const beforeTickIsExpired = yield select(selectors.cart.makeSelectIsExpired());
        yield take(actions.app.ActionTypes.TICK);
        const afterTickIsExpired = yield select(selectors.cart.makeSelectIsExpired());

        if (beforeTickIsExpired === false && afterTickIsExpired === false) {
            const diff = yield select(selectors.cart.makeSelectExpireDiff());
            if (diff.totalSeconds === 300) {
                yield put(actions.cart.onExpiringTimeLeft5m());
            } else if (diff.totalSeconds === 60) {
                yield put(actions.cart.onExpiringTimeLeft1m());
            }
        }

        // expired
        if (beforeTickIsExpired === false && afterTickIsExpired === true) {
            yield put(actions.cart.onExpired());
        }
    }
}

export function* watchItemStateChangeOrRemove() {
    const tasks = {};
    while (true) {
        const action = yield take([
            actions.cart.ActionTypes.ITEM_STATE_IS_CHANGING,
            actions.cart.ActionTypes.ITEM_STATE_IS_REMOVING,
        ]);
        if (tasks[action.item.id]) {
            yield cancel(tasks[action.item.id]);
            tasks[action.item.id] = undefined;
        }

        if (action.type === actions.cart.ActionTypes.ITEM_STATE_IS_CHANGING) {
            tasks[action.item.id] = yield fork(onItemStateChange, action);
        } else if (action.type === actions.cart.ActionTypes.ITEM_STATE_IS_REMOVING) {
            tasks[action.item.id] = yield fork(onItemStateRemove, action);
        }
    }
}

export function* onItemStateChange({ item }) {
    try {
        // in this implementation,
        // we immediately send the user to the page
        // and try to process the deletion request,
        // we hope that it will be successful
        yield put(actions.cart.changeItemState.isChanging(item.id, true));

        // maybe we already have the data and the container doesn't need to load it
        const activities = yield select(selectors.cart.makeSelectActivities());
        const activity = activities.find(
            (activityItem) => parseInt(activityItem.id) === parseInt(item.product_id)
        );

        if (activity && activity.slug) {
            yield put(activityPageActions.resetContents());
            yield put(
                activityPageActions.setParams({
                    id: activity.id,
                    slug: activity.slug,
                })
            );
            yield put(activityPageActions.fetchPageSuccessfully(activity));
        }

        yield put(actions.app.pushLocation(item.product_url));
        yield delay(1);
        const element = document.getElementById('availability');
        if (element) {
            const moveTo = new MoveToElement();
            moveTo.move(element);
        }

        // debounce
        yield delay(150);
        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const res = yield api.cart.removeItem({
            session_id: sessionId,
            cart_item_id: item.id,
        });
        if (!res.success) {
            const errors = Array.isArray(res.errors)
                ? res.errors
                : ['Failed to remove item from cart'];

            throw new ExternalUserValidationError(errors.join(', '));
        } else {
            yield put(actions.cart.removeItemById(item.id, parseInt(item.product_id, 10)));
        }
    } catch (err) {
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeItemState.isChanging(item.id, false));
        yield put(actions.cart.backgroundFetch());
    }
}

export function* onItemStateRemove({ item }) {
    try {
        // in this implementation,
        // we immediately send the user to the page
        // and try to process the deletion request,
        // we hope that it will be successful
        yield put(actions.cart.changeItemState.isRemoving(item.id, true));

        // debounce
        yield delay(150);
        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const res = yield api.cart.removeItem({
            session_id: sessionId,
            cart_item_id: item.id,
        });
        if (!res.success) {
            const errors = Array.isArray(res.errors)
                ? res.errors
                : ['Failed to remove item from cart'];

            throw new ExternalUserValidationError(errors.join(', '));
        } else {
            yield put(actions.cart.removeItemById(item.id, parseInt(item.product_id, 10)));
        }
    } catch (err) {
        yield put(actions.app.error(err));
    } finally {
        yield put(actions.cart.changeItemState.isRemoving(item.id, false));
        yield put(actions.cart.backgroundFetch());
    }
}

export function* watchUpdateSessionId() {
    while (true) {
        yield take(actions.cart.ActionTypes.UPDATE_SESSION_ID);
        const token = shuffle(ulid().split('')).join('');
        saveTokenCart(token);
        yield put(actions.cart.changeSessionId(token));
    }
}

export function* watchExpiring() {
    while (true) {
        yield take([
            actions.cart.ActionTypes.ON_EXPIRING_TIME_LEFT_1M,
            actions.cart.ActionTypes.ON_EXPIRING_TIME_LEFT_5M,
        ]);
        yield put(actions.cart.changeExpireModalIsVisible(true));
    }
}

export function* watchExpired() {
    while (true) {
        yield take([actions.cart.ActionTypes.ON_EXPIRED]);
        yield put(actions.cart.changeExpireModalIsVisible(false));
    }
}

export function* watchAndAutoApplyPromocode() {
    const chan = yield actionChannel([
        actions.cart.ActionTypes.ON_END_FETCH,
        actions.cart.ActionTypes.ON_END_BACKGROUND_FETCH,
        actions.promocode.ActionTypes.ON_VALUE_HAS_BEEN_SET,
        actions.promocode.ActionTypes.ON_VALUE_HAS_BEEN_RESET,
    ]);

    while (true) {
        yield take(chan);
        yield call(onAutoApplyPromocode);
    }
}

export function* onAutoApplyPromocode() {
    try {
        const sessionId = yield select(selectors.cart.makeSelectSessionId());
        const data = yield select(selectors.cart.makeSelectData());
        const isSad = yield select(selectors.cart.makeSelectIsSad());
        const promocode = yield select(selectors.promocode.makeSelectValue());

        const isValidCart = Boolean(!!data.id && !isSad);
        const isAlreadyApplied = isPlainObject(data.discount) && !!data.discount.promo;

        if (isValidCart && !isAlreadyApplied && promocode) {
            yield put(actions.cart.changeIsApplying(true));

            try {
                const res = yield api.promocode.apply({
                    session_id: sessionId,
                    code: promocode,
                });

                if (!res.success) {
                    const errors = Array.isArray(res.errors)
                        ? res.errors
                        : ['Failed apply promo to cart'];

                    throw new ExternalUserValidationError(errors.join(', '));
                } else {
                    yield put(actions.cart.backgroundFetch());
                }
            } catch (err) {
                throw err;
            } finally {
                yield put(actions.cart.changeIsApplying(false));
            }
        }
    } catch (err) {
        yield put(actions.app.error(err));
    }
}

export function* initialize() {
    yield put(actions.cart.changeSessionId(loadTokenCart()));
    const sessionId = yield select(selectors.cart.makeSelectSessionId());
    const isLoaded = yield select(selectors.cart.makeSelectIsLoaded());
    const itemProductIdsFromStore = get(LS_CART_ITEM_PRODUCT_IDS);

    const itemProductIds =
        Array.isArray(itemProductIdsFromStore) && itemProductIdsFromStore.length > 0
            ? itemProductIdsFromStore.map((v) => Number(v)).filter(Boolean)
            : [];
    yield put(actions.cart.changeItemProductIds(itemProductIds));
    yield fork(fetchOutdated);

    function* resetSession() {
        yield put(actions.cart.clearActivities());
        yield put(actions.cart.updateSessionId());
        yield put(actions.cart.changeItems([]));
        yield put(actions.cart.changeIsLoaded(true));
    }

    if (!sessionId) {
        yield call(resetSession);
    } else if (!isLoaded) {
        yield put(actions.cart.fetch());
        yield take([
            actions.cart.ActionTypes.ON_END_FETCH,
            actions.cart.ActionTypes.ON_END_BACKGROUND_FETCH,
        ]);

        const isExpired = yield select(selectors.cart.makeSelectIsExpired());

        if (!isExpired) {
            const diff = yield select(selectors.cart.makeSelectExpireDiff());

            if (diff.totalSeconds <= 60) {
                yield put(actions.cart.onExpiringTimeLeft1m());
            } else if (diff.totalSeconds <= 300) {
                yield put(actions.cart.onExpiringTimeLeft5m());
            }

            const cartItems = yield select(selectors.cart.makeSelectItems());
            if (window.location.pathname.search('/cart') < 0 && cartItems.length > 0) {
                const text = cartItems.length === 1 ? 'item' : 'items';
                callToast(
                    <ToastContent
                        header="Shopping Cart"
                        text={`Don't forget, you have ${cartItems.length} ${text} in your cart.`}
                        icon="shopping-cart"
                        link="/cart/"
                        button="Cart"
                    />
                );
            }
        } else {
            yield call(resetSession);
        }
    }
}

export default function* cartSaga() {
    yield all([
        fork(watchFetch),
        fork(watchEndFetch),
        fork(watchUpdateSessionId),
        fork(watchExpireTick),
        fork(watchAddResolve),
        fork(watchItemStateChangeOrRemove),
        fork(watchExpiring),
        fork(watchExpired),
        fork(watchExtend),
        fork(watchAndAutoApplyPromocode),
        fork(watchApplyDiscount),
        fork(watchRemoveDiscount),
        fork(initialize),
    ]);
}
